/* eslint-disable no-restricted-imports */
import { Button, Flex } from "@chakra-ui/react";
import { Form, Formik } from "formik";
import React from "react";
import { Card } from "src/components/Layout/Card";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import * as AF from "src/types/formTemplate";
import * as RD from "src/types/remoteData";
import { getNewVerificationId, isNewVerification } from "../../../contents";
import { useEditFormTemplateContext } from "../../../context";
import { ImportError } from "../../ImportError";
import { LiveEditConfirmationProvider } from "../../LiveEditConfirmation";
import { QuestionVisibility } from "../../QuestionVisibility";
import { ValidationAlert } from "../../ValidationAlert";
import { DirtyFormTracker } from "../DirtyFormTracker";
import {
  FormikType,
  toDeletedVerificationIds,
  toFormikValues,
  toQuestion,
  toVerification,
  validateForm,
} from "../formik";
import { ConfigureQuestion } from "../QuestionConfiguration/ConfigureQuestion";
import { SchoolItem } from "../types";
import { AddressQuestionForm } from "./AddressQuestion";
import { AnswersOptions } from "./AnswersOptions";
import { GradeQuestionForm } from "./GradeQuestion";
import { PermissionLevel } from "./PermissionLevel";
import { QuestionConstraints, QuestionFormat } from "./QuestionContraints";
import { QuestionKey } from "./QuestionKey";
import { QuestionLink } from "./QuestionLink";
import { QuestionTitle } from "./QuestionTitle";
import { QuestionType } from "./QuestionType";

export type QuestionFormEditProps = {
  schools: RD.RemoteData<unknown, SchoolItem[]>;
  onCancelButtonClick: () => void;
  onUpdate: (
    question: AF.V2.Question,
    verification: AF.V2.FormVerification | undefined,
    deletedVerificationIds: string[] | undefined
  ) => Promise<void>;
  question: AF.V2.Question;
  sectionType: AF.V2.SectionType;
  otherQuestions: readonly AF.V2.Question[];
};

export function QuestionFormEdit({
  onCancelButtonClick,
  onUpdate,
  schools,
  question,
  sectionType,
  otherQuestions,
}: QuestionFormEditProps) {
  const { state } = useEditFormTemplateContext();
  const verifications: AF.V2.FormVerification[] = React.useMemo(
    () => state.get("draft").verifications ?? [],
    [state]
  );
  const initialValues: FormikType = React.useMemo(
    () => toFormikValues(sectionType, question, verifications),
    [question, verifications, sectionType]
  );

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const toast = useAvelaToast();
  const { grades } = useEditFormTemplateContext();

  const onSubmit = React.useCallback(
    async (values: FormikType) => {
      setIsSubmitting(true);

      const updatedQuestion = toQuestion(question, values, grades);
      const updatedVerification = toVerification(values);
      const deletedVerificationIds = toDeletedVerificationIds(values);

      // if the verification is new, we need to generate a new id
      // and update the question with the new verification id
      if (updatedVerification && isNewVerification(updatedVerification)) {
        updatedVerification.id = getNewVerificationId();
        updatedQuestion.formVerificationId = updatedVerification.id;
      }

      try {
        await onUpdate(
          updatedQuestion,
          updatedVerification,
          deletedVerificationIds
        );
      } catch (err: unknown) {
        console.error(err);
        toast.genericError();
      } finally {
        setIsSubmitting(false);
        document.getElementById(question.id)?.scrollIntoView();
      }
    },
    [grades, onUpdate, question, toast]
  );

  return (
    <Card
      display="flex"
      flexDirection="column"
      showBorder
      gap="3"
      padding={6}
      id={question.id}
    >
      <ValidationAlert id={question.id} />
      <ImportError id={question.id} />
      <Formik<FormikType>
        initialValues={initialValues}
        onSubmit={onSubmit}
        validate={validateForm(
          sectionType,
          state.get("draft"),
          schools.toNullable()
        )}
      >
        {(formik) => {
          let questionForm;
          switch (formik.values.type) {
            case AF.V2.QuestionTypes.Grades:
              questionForm = (
                <GradeQuestionForm
                  sectionType={sectionType}
                  questionId={question.id}
                  otherQuestions={otherQuestions}
                  schools={schools}
                />
              );
              break;

            case AF.V2.QuestionTypes.Address:
              questionForm = (
                <AddressQuestionForm
                  questionId={question.id}
                  schools={schools}
                  sectionType={sectionType}
                  otherQuestions={otherQuestions}
                />
              );
              break;

            default:
              questionForm = (
                <>
                  <Flex direction="row" gap="4">
                    <ConfigureQuestion questionId={question.id} />
                  </Flex>
                  <Flex direction="column" flexGrow="1" gap="3">
                    <QuestionLink questionId={question.id} />
                    <PermissionLevel questionId={question.id} />
                    <QuestionConstraints questionId={question.id} />
                    <QuestionVisibility
                      sectionType={sectionType}
                      otherQuestions={otherQuestions}
                      schools={schools}
                    />
                    <AnswersOptions
                      sectionType={sectionType}
                      schools={schools}
                      question={question}
                      otherQuestions={otherQuestions}
                    />
                  </Flex>
                </>
              );
              break;
          }
          return (
            <LiveEditConfirmationProvider questionId={question.id}>
              <Flex as={Form} direction="column" gap="6">
                <DirtyFormTracker />
                <QuestionTitle questionId={question.id} />
                <QuestionKey
                  questionId={question.id}
                  otherQuestions={otherQuestions}
                />
                <QuestionType
                  questionId={question.id}
                  sectionType={sectionType}
                />
                <QuestionFormat questionId={question.id} />
                {questionForm}
                <Flex gap="3" justifyContent="right" paddingY="3">
                  <Button
                    variant="ghost"
                    colorScheme="gray"
                    onClick={onCancelButtonClick}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" isLoading={isSubmitting}>
                    Update question
                  </Button>
                </Flex>
              </Flex>
            </LiveEditConfirmationProvider>
          );
        }}
      </Formik>
    </Card>
  );
}
